import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { ArrowIcon } from '../../common/icons/arrow-icon'

enum timeTypes {
    HOUR = 'hour',
    MINUTE = 'minute',
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2px;
    align-items: center;
    justify-content: center;
    .arrowBtn {
        path {
            transition: 0.2s ease all;
        }

        &:hover {
            cursor: pointer;

            path {
                fill: ${({ theme }) => theme.brandColors.CA_Green};
            }
        }
    }

    .rotate {
        transform: rotate(180deg);
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type='number'] {
        -moz-appearance: textfield;
    }
`

const TimeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`

export type TimePickerProps = {
    defaultHour?: number
    defaultMinute?: number
    setTime: (hour: number, minute: number) => void
}

export const TimePicker = React.memo(function TimePicker({
    defaultHour,
    defaultMinute,
    setTime,
}: TimePickerProps): React.ReactElement {
    const formatTime = (value: number, type?: timeTypes) => {
        if (value < 0) return '00'
        if ((type === timeTypes.HOUR && value > 23) || (type === timeTypes.MINUTE && value > 60)) return '00'
        if (value < 10) return '0' + Number(value)
        return value.toString()
    }

    const [hour, setHour] = useState(formatTime(defaultHour ?? 0, timeTypes.HOUR))
    const [minute, setMinute] = useState(formatTime(defaultMinute ?? 0, timeTypes.MINUTE))

    useEffect(() => {
        setTime(Number(hour), Number(minute))
    }, [hour, minute])

    const increaseTime = (value: string, type: timeTypes) => {
        const tempValue = Number(value)

        if (type === 'minute') {
            if (tempValue === 59) {
                setMinute('00')

                if (Number(hour) + 1 === 24) setHour('00')
                else setHour(formatTime(Number(hour) + 1))
            } else {
                setMinute(formatTime(tempValue + 1))
            }
        }
        if (type === 'hour') {
            if (tempValue === 23) setHour('00')
            else setHour(formatTime(tempValue + 1))
        }
    }

    const decreaseTime = (value: string, type: timeTypes) => {
        const tempValue = Number(value)

        if (type === timeTypes.MINUTE) {
            if (tempValue === 0) {
                setMinute('59')
                if (Number(hour) - 1 === -1) setHour('23')
                else setHour(formatTime(Number(hour) - 1))
            } else {
                setMinute(formatTime(tempValue - 1))
            }
        }
        if (type === timeTypes.HOUR) {
            if (tempValue === 0) setHour('23')
            else setHour(formatTime(tempValue - 1))
        }
    }

    return (
        <Wrapper>
            <TimeContainer>
                <div onClick={() => increaseTime(hour, timeTypes.HOUR)} data-testid="increaseHours">
                    <ArrowIcon className="arrowBtn" color="#000" />
                </div>
                <input
                    value={hour}
                    className="form-control"
                    type="number"
                    onChange={(e) => setHour(e.target.value)}
                    onBlur={() => setHour(formatTime(Number(hour), timeTypes.HOUR))}
                    data-testid="hours"
                />
                <div onClick={() => decreaseTime(hour, timeTypes.HOUR)} data-testid="decreaseHours">
                    <ArrowIcon className="rotate arrowBtn" color="#000" />
                </div>
            </TimeContainer>{' '}
            :
            <TimeContainer>
                <div onClick={() => increaseTime(minute, timeTypes.MINUTE)} data-testid="increaseMinutes">
                    <ArrowIcon className="arrowBtn" color="#000" />
                </div>
                <input
                    value={minute}
                    className="form-control"
                    type="number"
                    onChange={(e) => setMinute(e.target.value)}
                    onBlur={() => setMinute(formatTime(Number(minute), timeTypes.MINUTE))}
                    data-testid="minutes"
                />
                <div onClick={() => decreaseTime(minute, timeTypes.MINUTE)} data-testid="decreaseMinutes">
                    <ArrowIcon className="rotate arrowBtn" color="#000" />
                </div>
            </TimeContainer>
        </Wrapper>
    )
})
